import { Link, useLocation } from 'react-router-dom';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { useCatalog } from '../../../contexts/CatalogContext';
import { deliveriesUntilGift, refillableFromOrders } from '../../../utils/account';
import { giftCardCheck, isAwaitingDelivery } from '../../../utils/accountOrders';
import { accountHeading } from '../../../ui/sections';
import { NextDeliveryCard } from '../cards/NextDeliveryCard';
import { OrderBump } from '../cards/OrderBump';
import { SubscribePrompt } from '../cards/SubscribePrompt';
import { SubscriptionPerks } from '../cards/SubscriptionPerks';
import { SubscriptionRhythm } from '../cards/SubscriptionRhythm';
import { SubscriptionStop } from '../cards/SubscriptionStop';

/** Het anker op Thuis (`/account/thuis#levering`); de mails en de knoppen op het overzicht springen hierheen. */
export const DELIVERY_ANCHOR = 'levering';

/**
 * Bewust Abonnement op Thuis (`#levering`): zelf regelen, zonder te bellen of te mailen.
 * - loopt of gepauzeerd: de levering, eenmalig erbij, ritme, voordelen en stoppen;
 * - geen (of gestopt) maar wel iets bij te vullen: het aanbod, rustig;
 * - niets bij te vullen: alleen uitleg als ze hier via een link landt.
 */
export function DeliverySection() {
  const {
    account
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const {
    hash
  } = useLocation();
  const sub = account.subscription;
  if (sub && sub.status !== 'gestopt') {
    const active = sub.status === 'actief';
    const untilGift = deliveriesUntilGift(sub);
    return <section id={DELIVERY_ANCHOR} aria-labelledby="delivery-section-title" className="scroll-mt-28" data-id="element-6300" data-mp="3dbw1uiozo07o">
        <h2 id="delivery-section-title" className={accountHeading} data-id="element-6301" data-mp="3r7hl098jjps6">
          Je levering
        </h2>
        <p className="mt-1 text-[15px] text-muted" data-id="element-6302" data-mp="3n5wc0yz4nvn9">Opschuiven, overslaan of pauzeren doe je hier in één tik. Stoppen kan ook, altijd.</p>
        {/* grid-cols-1 = minmax(0, 1fr): niets maakt de kolom op de telefoon breder dan het scherm. */}
        <div className="mt-5 grid grid-cols-1 gap-8 xl:grid-cols-12" data-id="element-6303" data-mp="2trv1ukgvvdm1">
          <div className="min-w-0 space-y-10 xl:col-span-8" data-id="element-6304" data-mp="2xsncni5elqkf">
            <NextDeliveryCard data-id="element-6305" data-mp="2xt6rgpzrbyu8" />
            <OrderBump data-id="element-6306" data-mp="32rqhp2ldvwse" />
          </div>
          <aside className="min-w-0 space-y-6 xl:col-span-4" aria-label="Ritme en voordelen" data-id="element-6307" data-mp="32qp68x0gh8a5">
            {active && <SubscriptionRhythm sub={sub} untilGift={untilGift} data-id="element-6308" data-mp="37ji7hawptyhh" />}
            <SubscriptionPerks untilGift={untilGift} data-id="element-6309" data-mp="3chti3nuu4d0r" />
            <SubscriptionStop active={active} data-id="element-6310" data-mp="3cha24coqvypd" />
          </aside>
        </div>
      </section>;
  }
  const isGift = giftCardCheck(productById);
  const waiting = account.orders.some(o => isAwaitingDelivery(o, isGift));
  const canStart = refillableFromOrders(account, productById).length > 0;

  // Bijvullen pas voorstellen als het pakket binnen is, niet terwijl ze er nog op wacht.
  if (canStart && !waiting) return <section id={DELIVERY_ANCHOR} aria-label="Automatisch bijvullen" className="scroll-mt-28 grid grid-cols-1 gap-6 xl:grid-cols-12" data-id="element-6311" data-mp="1w3bit7e15t1d">
        <div className="min-w-0 xl:col-span-7" data-id="element-6312" data-mp="1oku50oz4fs2l">
          <SubscribePrompt data-id="element-6313" data-mp="1ollpb3n9q3dk" />
        </div>
        <aside className="min-w-0 xl:col-span-5" aria-label="Voordelen" data-id="element-6314" data-mp="1jssjecg2cozw">
          <SubscriptionPerks data-id="element-6315" data-mp="1etz8mrs5vc3f" />
        </aside>
      </section>;
  if (hash !== `#${DELIVERY_ANCHOR}`) return null;
  return <section id={DELIVERY_ANCHOR} aria-labelledby="delivery-empty-title" className="scroll-mt-28 border-t border-line pt-6" data-id="element-6316" data-mp="19vny0eu1kxk5">
      <h2 id="delivery-empty-title" className="text-[15px] text-ink" data-id="element-6317" data-mp="27i4rf8znmr0m">
        {sub ? 'Je abonnement is gestopt' : 'Automatisch bijvullen'}
      </h2>
      <p className="mt-1 max-w-xl text-[14px] leading-relaxed text-muted" data-id="element-6318" data-mp="1xk75is59p6b6">
        {waiting ? 'Zodra je pakket binnen is, kun je hier de producten kiezen die vanzelf moeten komen.' : 'Zet in de winkelmand een product op “automatisch bijvullen”, dan regel je het daarna hier.'}{' '}
        <Link to="/webshop" className="text-ink underline underline-offset-4 hover:text-clay" data-id="element-6319" data-mp="i3ynd2r03dqv">
          Naar de webshop
        </Link>
      </p>
    </section>;
}